import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { User, Loader, Send, ArrowLeft } from 'lucide-react';
import { ref, get, push, update, onValue } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import { database, auth } from '../../shared/firebase';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

export default function Conversation() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const [user, setUser] = useState(null);
  const [conversation, setConversation] = useState(null);
  const [otherProfile, setOtherProfile] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      if (!currentUser) { navigate('/inloggen'); return; }
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, [navigate]);

  useEffect(() => {
    if (!user) return;

    const loadConversation = async () => {
      const convSnap = await get(ref(database, `vjb_contact_requests/${conversationId}`));
      if (!convSnap.exists()) { navigate('/berichten'); return; }
      
      const convData = convSnap.val();
      if (convData.fromUid !== user.uid && convData.toUid !== user.uid) {
        navigate('/berichten'); return;
      }
      
      setConversation(convData);
      
      const otherId = convData.fromUid === user.uid ? convData.toUid : convData.fromUid;
      const profileSnap = await get(ref(database, `vjb_profiles/${otherId}`));
      if (profileSnap.exists()) setOtherProfile(profileSnap.val());
      
      setLoading(false);
    };
    loadConversation();
  }, [user, conversationId, navigate]);

  useEffect(() => {
    if (!user || !conversation) return;

    // Listen for messages
    const messagesRef = ref(database, `vjb_messages/${conversationId}`);
    const unsubscribe = onValue(messagesRef, (snapshot) => {
      const allMessages = [];
      
      // Add initial message from conversation
      allMessages.push({
        id: 'initial',
        text: conversation.message,
        fromUid: conversation.fromUid,
        createdAt: conversation.createdAt
      });

      if (snapshot.exists()) {
        Object.entries(snapshot.val()).forEach(([id, msg]) => {
          allMessages.push({ id, ...msg });
        });
      }

      allMessages.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
      setMessages(allMessages);

      // Mark messages as read
      if (snapshot.exists()) {
        Object.entries(snapshot.val()).forEach(([id, msg]) => {
          if (msg.fromUid !== user.uid && !msg.readAt) {
            update(ref(database, `vjb_messages/${conversationId}/${id}`), {
              readAt: new Date().toISOString()
            });
          }
        });
      }
    });

    return () => unsubscribe();
  }, [user, conversation, conversationId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const newRef = push(ref(database, `vjb_messages/${conversationId}`));
      await update(newRef, {
        text: newMessage.trim(),
        fromUid: user.uid,
        createdAt: new Date().toISOString()
      });
      
      // Update last message time on conversation
      await update(ref(database, `vjb_contact_requests/${conversationId}`), {
        lastMessageAt: new Date().toISOString()
      });
      
      setNewMessage('');
    } catch (error) {
      console.error('Send error:', error);
    }
    setSending(false);
  };

  const formatTime = (dateString) => {
    return new Date(dateString).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) return 'Vandaag';
    if (date.toDateString() === yesterday.toDateString()) return 'Gisteren';
    return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'long' });
  };

  if (loading) {
    return <div className="min-h-screen bg-black flex items-center justify-center"><Loader className="animate-spin text-[#8146FF]" size={32} /></div>;
  }

  // Group messages by date
  const messagesByDate = messages.reduce((groups, msg) => {
    const date = formatDate(msg.createdAt);
    if (!groups[date]) groups[date] = [];
    groups[date].push(msg);
    return groups;
  }, {});

  return (
    <div className="min-h-screen bg-black text-white flex flex-col" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Custom header for conversation */}
      <header className="border-b border-white/10 bg-black/50 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-2xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-4 h-16">
            <Link to="/berichten" className="p-2 -ml-2 text-white/70 hover:text-white transition">
              <ArrowLeft size={24} />
            </Link>
            
            <Link to={`/muzikant/${conversation?.fromUid === user?.uid ? conversation?.toUid : conversation?.fromUid}`} className="flex items-center gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center overflow-hidden flex-shrink-0">
                {otherProfile?.photoUrl ? (
                  <img src={otherProfile.photoUrl} alt={otherProfile.displayName} className="w-full h-full object-cover" />
                ) : (
                  <User size={20} className="text-white/40" />
                )}
              </div>
              <div className="min-w-0">
                <h1 className="font-semibold truncate">{otherProfile?.displayName || 'Onbekend'}</h1>
                <p className="text-xs text-white/50 truncate">{conversation?.adTitle}</p>
              </div>
            </Link>
          </div>
        </div>
      </header>

      {/* Messages */}
      <main className="flex-1 max-w-2xl mx-auto px-4 md:px-6 py-4 w-full overflow-y-auto">
        {Object.entries(messagesByDate).map(([date, msgs]) => (
          <div key={date}>
            <div className="flex items-center justify-center my-4">
              <span className="text-xs text-white/40 bg-white/5 px-3 py-1 rounded-full">{date}</span>
            </div>
            
            {msgs.map((msg) => {
              const isOwn = msg.fromUid === user?.uid;
              return (
                <div key={msg.id} className={`flex mb-3 ${isOwn ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] px-4 py-3 rounded-2xl ${
                    isOwn 
                      ? 'bg-[#8146FF] text-white rounded-br-md' 
                      : 'bg-white/10 text-white rounded-bl-md'
                  }`}>
                    <p className="whitespace-pre-wrap break-words">{msg.text}</p>
                    <p className={`text-xs mt-1 ${isOwn ? 'text-white/60' : 'text-white/40'}`}>
                      {formatTime(msg.createdAt)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        ))}
        <div ref={messagesEndRef} />
      </main>

      {/* Input */}
      <div className="border-t border-white/10 bg-black/50 backdrop-blur-md">
        <div className="max-w-2xl mx-auto px-4 md:px-6 py-4">
          <form onSubmit={handleSend} className="flex gap-3">
            <input
              type="text"
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Typ een bericht..."
              maxLength={1000}
              className="flex-1 bg-white/10 border border-white/20 rounded-full px-5 py-3 text-white placeholder-white/40 focus:outline-none focus:border-[#8146FF]"
            />
            <button
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="bg-[#8146FF] hover:bg-[#6a35d9] text-white p-3 rounded-full transition disabled:opacity-50"
            >
              {sending ? <Loader className="animate-spin" size={20} /> : <Send size={20} />}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
